import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import MonthPickerDropdown from '../filters/core/MonthPickerDropdown';
import { usePanelPosition } from '../filters/hooks/usePanelPosition';
import { ANALYTICS_CONSTANTS } from '../constants';
import { MonthRange, countMonths, withEndMonth, withStartMonth } from './exportRange';

const PICKER_WIDTH = 256;
const PICKER_HEIGHT = 250;

interface MonthFieldProps {
  label: string;
  value?: Date;
  onSelect: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
}

const formatMonthYear = (date?: Date) =>
  date ? date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'Select month';

function MonthField({ label, value, onSelect, minDate, maxDate, disabled }: MonthFieldProps) {
  const labelId = useId();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  // Viewport strategy: fixed positioning keeps the picker from being clipped by the modal's scroll area.
  const position = usePanelPosition(anchorRef, isOpen, {
    strategy: 'viewport',
    maxWidth: PICKER_WIDTH,
    desktopMinWidth: PICKER_WIDTH,
    panelHeight: PICKER_HEIGHT,
  });

  const close = () => setIsOpen(false);

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!anchorRef.current?.contains(event.target as Node)) close();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isOpen]);

  return (
    <div>
      <span id={labelId} className="mb-1 block text-xs text-[var(--anl-text-muted)]">
        {label}
      </span>
      <div ref={anchorRef}>
        <button
          type="button"
          onClick={() => setIsOpen(open => !open)}
          disabled={disabled}
          aria-labelledby={labelId}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          className="flex h-10 w-full items-center justify-between rounded-md border border-[var(--anl-border)] bg-[var(--anl-surface-elev)] px-3 text-sm text-[var(--anl-text)] transition-colors hover:border-[var(--anl-text-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--anl-accent)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {formatMonthYear(value)}
          <ChevronDown
            className={`h-4 w-4 text-[var(--anl-text-muted)] transition-transform ${isOpen ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>
        {isOpen && position.type === 'fixed' && (
          <div
            role="dialog"
            aria-labelledby={labelId}
            className="fixed z-[60]"
            style={{
              top: position.top,
              bottom: position.bottom,
              left: position.left,
              width: position.width,
            }}
          >
            <MonthPickerDropdown
              selectedDate={value}
              onDateSelect={date => {
                onSelect(date);
                close();
              }}
              minDate={minDate}
              maxDate={maxDate}
              selectedColor="var(--anl-accent)"
            />
          </div>
        )}
      </div>
    </div>
  );
}

interface ExportMonthRangeProps {
  range: MonthRange;
  maxMonths: number;
  onChange: (range: MonthRange) => void;
  disabled?: boolean;
}

export default function ExportMonthRange({
  range,
  maxMonths,
  onChange,
  disabled = false,
}: ExportMonthRangeProps) {
  const earliest = new Date(ANALYTICS_CONSTANTS.EARLIEST_DATA_YEAR, 0, 1);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-3">
        <MonthField
          label="From"
          value={range.startDate}
          onSelect={date => onChange(withStartMonth(range, date, maxMonths))}
          minDate={earliest}
          disabled={disabled}
        />
        <MonthField
          label="To"
          value={range.endDate}
          onSelect={date => onChange(withEndMonth(range, date, maxMonths))}
          minDate={earliest}
          disabled={disabled}
        />
      </div>
      <p className="text-xs text-[var(--anl-text-muted)]">
        Up to {maxMonths} months. {countMonths(range)} selected.
      </p>
    </div>
  );
}
